import { useContext, useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import {
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import AppFormContainer from '../forms/AppFormContainer'
import AppFormSection from '../forms/AppFormSection'
import Tips from '../Tips'
import AuthContext from '../../units/auth/AuthContext'
import { fileToDataUrl, resolveUploadUrl } from '../../helpers/file.helper'
import announcementSchema from '../../schemas/announcement'
import { FREQUENCIES } from '../../data-organizers/announcements/frequencies'

const INTERNAL_TIPS = ['שדות אלה מיועדים לשימוש פנימי ואינם מוצגים למשתמשי הקצה.']

const MORE_INFO_LABEL = 'טקסט חלופי לקישור בסרגל (מידע נוסף)'

export const CONVERSION_BUTTON_TIPS = ['הכפתור יופיע רק אם הוא לא ריק']

export const ACTION_TIPS = [
    'שם הפעולה משמש לתיוג הנתונים בלבד ואינו מוצג למקבלי המודעה, וניתן לשנותו גם לאחר הפרסום.',
    'סוג הפעולה והיעד ננעלים עם פרסום המודעה, וניתן לשנותם רק בעותק חדש שלה.',
]

export const ACTION_KIND_LABELS = {
    none: 'ללא פעולה',
    url: 'פתיחת קישור',
    email: 'שליחת דוא"ל',
}

export const EMAIL_TEMPLATE_LABEL = 'תבנית דוא"ל'

export const IMAGE_ACTION_LABELS = {
    kind: 'פעולה בלחיצה על התמונה',
    url: 'כתובת לפתיחה (תמונה)',
    name: 'שם הפעולה (תמונה)',
}

export const BUTTON_ACTION_LABELS = {
    kind: 'פעולה בלחיצה על הכפתור',
    url: 'כתובת לפתיחה (כפתור)',
    name: 'שם הפעולה (כפתור)',
}

function textFieldFor(formik, name, label, extra = {}) {
    return (
        <TextField
            id={name}
            name={name}
            label={label}
            value={formik.values[name] ?? ''}
            onChange={formik.handleChange}
            error={Boolean(formik.touched[name] && formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            fullWidth
            {...extra}
        />
    )
}

function ActionFields({ formik, trigger, labels, locked }) {
    const kindField = `${trigger}ActionKind`
    const kind = formik.values[kindField] ?? 'none'

    return (
        <Stack spacing={2}>
            {textFieldFor(formik, kindField, labels.kind, {
                select: true,
                disabled: locked,
                value: kind,
                slotProps: { select: { native: true } },
                children: Object.entries(ACTION_KIND_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                )),
            })}
            {textFieldFor(formik, `${trigger}ActionUrl`, labels.url, {
                disabled: locked || kind !== 'url',
            })}
            {textFieldFor(formik, `${trigger}ActionName`, labels.name)}
        </Stack>
    )
}

export default function AnnouncementForm({
    announcement,
    onSubmit,
}) {
    const locked = Boolean(announcement.isContentLocked)

    return (
        <Formik
            initialValues={announcement}
            validationSchema={announcementSchema}
            enableReinitialize
            onSubmit={(values) => onSubmit?.(values)}
        >
            {(formik) => (
                <Form id="form">
                    <AppFormContainer>
                        <AppFormSection title="תזמון" xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} lg={6}>
                                    <FormControl>
                                        <FormLabel id="frequency">תדירות</FormLabel>
                                        <RadioGroup
                                            name="frequency"
                                            aria-labelledby="frequency"
                                            value={formik.values.frequency ?? 'oncePerUser'}
                                            onChange={formik.handleChange}
                                            sx={{
                                                flexDirection: {
                                                    xs: 'column',
                                                    lg: 'row',
                                                },
                                            }}
                                        >
                                            {FREQUENCIES.map(({ value, label }) => (
                                                <FormControlLabel
                                                    key={value}
                                                    value={value}
                                                    label={label}
                                                    control={<Radio />}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <Stack
                                        direction={{
                                            xs: 'column',
                                            lg: 'row',
                                        }}
                                        spacing={2}
                                    >
                                        {textFieldFor(formik, 'startAt', 'תחילת תזמון', {
                                            type: 'datetime-local',
                                            slotProps: { inputLabel: { shrink: true } },
                                        })}
                                        {textFieldFor(formik, 'endAt', 'סיום תזמון', {
                                            type: 'datetime-local',
                                            slotProps: { inputLabel: { shrink: true } },
                                        })}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </AppFormSection>

                        <Grid
                            item
                            container
                            spacing={2}
                            xs={12}
                            lg={6}
                            alignContent="flex-start"
                        >
                            <AppFormSection title="כללי" xs={12}>
                                <Stack spacing={2}>
                                    <Tips tips={INTERNAL_TIPS} />
                                    {textFieldFor(formik, 'name', 'שם')}
                                    {textFieldFor(formik, 'notes', 'הערות', {
                                        multiline: true,
                                        minRows: 2,
                                    })}
                                </Stack>
                            </AppFormSection>

                            <AppFormSection title="תוכן המודעה" xs={12}>
                                <Stack spacing={2}>
                                    {textFieldFor(formik, 'title', 'כותרת', { disabled: locked })}
                                    {textFieldFor(formik, 'bodyText', 'תוכן', {
                                        disabled: locked,
                                        multiline: true,
                                        minRows: 4,
                                    })}

                                    <TextField
                                        id="image"
                                        name="image"
                                        label="תמונה"
                                        type="file"
                                        disabled={locked}
                                        onChange={(event) => {
                                            const [file] = event.target.files ?? []
                                            formik.setFieldValue('imageFile', file ?? null)
                                        }}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        fullWidth
                                    />

                                    <Tips tips={CONVERSION_BUTTON_TIPS} />
                                    {textFieldFor(formik, 'conversionButtonText', 'כיתוב כפתור התגובה', {
                                        disabled: locked,
                                    })}
                                    {textFieldFor(formik, 'conversionMessage', 'הודעת אישור לאחר לחיצה', {
                                        disabled: locked,
                                        multiline: true,
                                        minRows: 2,
                                    })}
                                </Stack>
                            </AppFormSection>

                            <AppFormSection title="פעולות" xs={12}>
                                <Stack spacing={2}>
                                    <Tips tips={ACTION_TIPS} />

                                    <ActionFields
                                        formik={formik}
                                        trigger="image"
                                        labels={IMAGE_ACTION_LABELS}
                                        locked={locked}
                                    />

                                    <ActionFields
                                        formik={formik}
                                        trigger="button"
                                        labels={BUTTON_ACTION_LABELS}
                                        locked={locked}
                                    />

                                    {textFieldFor(formik, 'emailTemplate', EMAIL_TEMPLATE_LABEL, {
                                        disabled: locked,
                                        multiline: true,
                                        minRows: 3,
                                    })}
                                </Stack>
                            </AppFormSection>

                            <AppFormSection title="סרגל" xs={12}>
                                <Stack spacing={2}>
                                    {textFieldFor(formik, 'caption', 'טקסט בסרגל', { disabled: locked })}
                                    {textFieldFor(formik, 'moreInfoText', MORE_INFO_LABEL, { disabled: locked })}
                                </Stack>
                            </AppFormSection>
                        </Grid>

                        <AppFormSection
                            title="תצוגה מקדימה"
                            xs={12}
                            lg={6}
                            sx={{
                                '& .MuiCard-root': {
                                    boxShadow: 'none',
                                    backgroundColor: 'grey.400',
                                },
                                '& .MuiCardContent-root, & .MuiCardContent-root:last-child': {
                                    padding: 0,
                                },
                            }}
                        >
                            <AnnouncementPreview
                                title={formik.values.title}
                                bodyText={formik.values.bodyText}
                                image={announcement.image}
                                chosen={formik.values.imageFile}
                            />
                        </AppFormSection>
                    </AppFormContainer>
                </Form>
            )}
        </Formik>
    )
}

function AnnouncementPreview({ title, bodyText, image, chosen }) {
    const token = useContext(AuthContext)?.token
    const [chosenUrl, setChosenUrl] = useState(null)

    useEffect(() => {
        if (chosen == null) {
            setChosenUrl(null)
            return undefined
        }

        let showing = true

        fileToDataUrl(chosen).then((dataUrl) => {
            if (showing) {
                setChosenUrl(dataUrl)
            }
        })

        return () => {
            showing = false
        }
    }, [chosen])

    const src = chosenUrl ?? resolveUploadUrl(image?.url, token)

    return (
        <Stack
            spacing={1}
            component="article"
            data-testid="announcement-preview"
            sx={{
                padding: 2,
                border: 1,
                borderColor: 'grey.400',
                borderRadius: 1,
                backgroundColor: 'common.white',
            }}
        >
            <Typography variant="h5" component="h1">{title}</Typography>

            <Typography variant="body2">{bodyText}</Typography>

            {src && (
                <Box
                    component="img"
                    src={src}
                    alt="תמונת המודעה"
                    sx={{
                        display: 'block',
                        maxWidth: '100%',
                    }}
                />
            )}
        </Stack>
    )
}
